import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ROLE_LABEL } from '../../api/client';
import { toast } from 'sonner';

type Conv = { remote_jid: string; last_at: string; n: number; last_text: string | null };
type Msg = { id: string; from_me: boolean; text: string; sender_name: string | null; sent_at: string };

// ATENDIMENTO (ex-aba WhatsApp): conversas do MVP.
// Escopo: adm/gestor veem TODAS; vendedor vê apenas as conversas dos leads dele.
export default function Atendimento() {
  const [convs, setConvs] = useState<Conv[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get<{ conversations: Conv[] }>('/messages/whatsapp/conversations');
      setConvs(d.conversations);
    } catch {
      setConvs([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadMsgs = useCallback(async (jid: string) => {
    try {
      const d = await api.get<{ messages: Msg[] }>(`/messages/whatsapp/messages/${encodeURIComponent(jid)}`);
      setMsgs(d.messages);
    } catch {
      setMsgs([]);
      setNote('Conversa não encontrada.');
    }
  }, []);

  useEffect(() => {
    if (sel) void loadMsgs(sel);
  }, [sel, loadMsgs]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [msgs]);

  async function enviar() {
    if (!sel || !text.trim()) return;
    try {
      await api.post('/messages/whatsapp/send-jid', { jid: sel, text });
      setText('');
      await loadMsgs(sel);
      await load();
    } catch (err) {
      setNote((err as { message?: string }).message ?? 'Falha no envio');
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
      <aside className="rounded-lg border border-[var(--border)] bg-white max-h-[70vh] overflow-y-auto">
        <header className="border-b border-[var(--border)] px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--text-dim)]">Conversas</header>
        {convs.length === 0 && <p className="p-4 text-xs text-[var(--text-dim)]">Sem conversas ainda. Elas aparecem quando o cliente responde (webhook Evolution).</p>}
        {convs.map((c) => (
          <button key={c.remote_jid} onClick={() => { setSel(c.remote_jid); setNote(null); }}
            className={sel === c.remote_jid ? 'block w-full border-b border-[var(--border)] p-3 text-left bg-[var(--bg-subtle)]' : 'block w-full border-b border-[var(--border)] p-3 text-left hover:bg-[var(--bg-subtle)]'}>
            <span className="block text-xs font-medium">{(c.remote_jid.split('@')[0] || '').replace(/^55(\d{2})(\d{4,5})/, '+55 $1 $2')}</span>
            <span className="block truncate text-[11px] text-[var(--text-dim)]">{c.last_text ?? ''}</span>
          </button>
        ))}
      </aside>
      <section className="rounded-lg border border-[var(--border)] bg-white flex flex-col min-h-[420px]">
        <header className="border-b border-[var(--border)] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--text-dim)]">
          {sel ? sel.split('@')[0] : 'Selecione uma conversa'}
        </header>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {msgs.map((m) => (
            <div key={m.id} className={m.from_me ? 'flex justify-end' : 'flex justify-start'}>
              <div className={m.from_me ? 'max-w-[70%] rounded-xl rounded-tr-sm bg-zinc-900 px-3 py-1.5 text-xs text-white' : 'max-w-[70%] rounded-xl rounded-tl-sm bg-zinc-100 px-3 py-1.5 text-xs'}>
                {m.text}
                <time className="ml-2 inline-block text-[10px] opacity-60">{new Date(m.sent_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</time>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <footer className="border-t border-[var(--border)] p-3 flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void enviar(); }}
            placeholder="Mensagem..."
            disabled={!sel}
            className="flex-1 h-10 rounded-lg border border-[var(--border-strong)] bg-white px-3 text-sm"
          />
          <button onClick={() => void enviar()} disabled={!sel || !text.trim()}
            className="h-10 rounded-lg bg-[var(--accent)] px-5 text-sm font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">Enviar</button>
        </footer>
        {note && <p className="text-xs text-red-claro px-3 pb-2">{note}</p>}
      </section>
    </div>
  );
}
